/**
 * Translation Storage Module
 *
 * Uses IndexedDB (via idb-keyval) to persist translated paragraphs.
 * This enables caching translations to avoid re-translating pages.
 *
 * Follows the same pattern as pdfStorage.ts
 */

import { createStore, get, set, del, keys } from "idb-keyval";
import {
  PdfParagraph,
  TranslatedParagraph,
  StoredPageTranslation,
} from "./translationTypes.js";

// Create dedicated store for translations
const translationStore = createStore("orbit-pdf-translations", "store");

/**
 * Generate storage key for a page's translation.
 * Format: `${docId}:page:${pageIndex}`
 */
function pageKey(docId: string, pageIndex: number): string {
  return `${docId}:page:${pageIndex}`;
}

/**
 * Save translation data for a single page.
 */
export async function savePageTranslation(
  docId: string,
  pageIndex: number,
  paragraphs: PdfParagraph[],
  translations: TranslatedParagraph[]
): Promise<void> {
  const stored: StoredPageTranslation = {
    docId,
    pageIndex,
    paragraphs,
    translations,
    translatedAt: Date.now(),
  };
  await set(pageKey(docId, pageIndex), stored, translationStore);
}

/**
 * Load translation data for a single page.
 * Returns undefined if page hasn't been translated.
 */
export async function loadPageTranslation(
  docId: string,
  pageIndex: number
): Promise<StoredPageTranslation | undefined> {
  return get<StoredPageTranslation>(pageKey(docId, pageIndex), translationStore);
}

/**
 * Check if a page has been translated.
 */
export async function hasPageTranslation(
  docId: string,
  pageIndex: number
): Promise<boolean> {
  const stored = await get<StoredPageTranslation>(
    pageKey(docId, pageIndex),
    translationStore
  );
  return stored !== undefined;
}

/**
 * Load translations for multiple pages at once.
 * Returns only pages that have cached translations.
 */
export async function loadPageTranslations(
  docId: string,
  pageIndices: number[]
): Promise<StoredPageTranslation[]> {
  const results = await Promise.all(
    pageIndices.map((pageIndex) => loadPageTranslation(docId, pageIndex))
  );
  return results.filter(
    (r): r is StoredPageTranslation => r !== undefined
  );
}

/**
 * Clear all translations for a document.
 */
export async function clearDocumentTranslations(docId: string): Promise<void> {
  const allKeys = await keys(translationStore);
  const docKeys = allKeys.filter(
    (key) => typeof key === "string" && key.startsWith(`${docId}:`)
  );
  await Promise.all(docKeys.map((key) => del(key, translationStore)));
}

/**
 * Clear a single page's translation.
 */
export async function clearPageTranslation(
  docId: string,
  pageIndex: number
): Promise<void> {
  await del(pageKey(docId, pageIndex), translationStore);
}

/**
 * Get list of translated page indices for a document.
 */
export async function getTranslatedPageIndices(
  docId: string
): Promise<number[]> {
  const allKeys = await keys(translationStore);
  const prefix = `${docId}:page:`;
  return allKeys
    .filter((key) => typeof key === "string" && key.startsWith(prefix))
    .map((key) => parseInt((key as string).slice(prefix.length), 10))
    .sort((a, b) => a - b);
}
